import React from 'react';

import type { ButtonProps as MantineButtonProps, GroupProps, ModalProps } from '@mantine/core';
import { Container, Flex, Group } from '@mantine/core';
import { useModals } from '@mantine/modals';

import { Divider } from '@/components/ui/Divider';

import Button, { type ButtonProps } from 'components/Button';

type ConfirmLabels = { confirm?: React.ReactNode; cancel?: React.ReactNode };

interface ConfirmModalProps {
  id?: string;
  children?: React.ReactNode;
  onCancel?: () => void;
  onConfirm?: () => void;
  closeOnConfirm?: boolean;
  closeOnCancel?: boolean;
  cancelProps?: MantineButtonProps & React.ComponentPropsWithoutRef<'button'> & Record<`data-${string}`, string>;
  confirmProps?: MantineButtonProps & React.ComponentPropsWithoutRef<'button'> & Record<`data-${string}`, string>;
  groupProps?: GroupProps;
  labels?: ConfirmLabels;
}

type ModalSettings = Partial<Omit<ModalProps, 'opened'>> & {
  modalId?: string;
};

export type OpenConfirmModalProps = ModalSettings &
  Omit<ConfirmModalProps, 'groupProps'> & {
    id: string;
    cancelProps?: Extract<ButtonProps, { as?: 'button' }>;
    confirmProps?: Extract<ButtonProps, { as?: 'button' }>;
    leftButtonGruopProps?: GroupProps;
    rightButtonGruopProps?: GroupProps;
  };

export const ConfirmModalTemplate = ({
  id,
  cancelProps,
  confirmProps,
  labels = { cancel: 'cancel', confirm: 'confirm' },
  closeOnConfirm = true,
  closeOnCancel = true,
  leftButtonGruopProps,
  rightButtonGruopProps,
  onCancel,
  onConfirm,
  children,
}: OpenConfirmModalProps) => {
  const { cancel: cancelLabel, confirm: confirmLabel } = labels;
  const ctx = useModals();

  const handleCancel = (event: React.MouseEvent<HTMLButtonElement>) => {
    typeof cancelProps?.onClick === 'function' && cancelProps?.onClick(event);
    typeof onCancel === 'function' && onCancel();
    closeOnCancel && ctx.closeModal(id);
  };

  const handleConfirm = (event: React.MouseEvent<HTMLButtonElement>) => {
    typeof confirmProps?.onClick === 'function' && confirmProps?.onClick(event);
    typeof onConfirm === 'function' && onConfirm();
    closeOnConfirm && ctx.closeModal(id);
  };

  return (
    <Flex direction="column" h="100%">
      {children}
      <Divider mt="42px" mb="13px" />
      <Group grow={true}>
        <Container>
          <Group justify="flex-start" gap="6px" {...leftButtonGruopProps}>
            {leftButtonGruopProps?.children}
          </Group>
        </Container>
        <Container>
          <Group justify="flex-end" gap="6px" {...rightButtonGruopProps}>
            {rightButtonGruopProps?.children ?? (
              <>
                <Button onClick={handleCancel} variant="outline" {...cancelProps}>
                  {cancelProps?.children || cancelLabel}
                </Button>
                <Button onClick={handleConfirm} variant="fill" {...confirmProps}>
                  {confirmProps?.children || confirmLabel}
                </Button>
              </>
            )}
          </Group>
        </Container>
      </Group>
    </Flex>
  );
};
